import Parse from "parse"
import dayjs from "dayjs"

import { getProductionStepExecution, getPseWithSectionOutputs } from "../../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import { computePackagingDatesData } from "../PackagingDateWeightDistribution"

const getPrintDateLabel = (date, stepProductionDate) => {
  const d = dayjs(date)
  const diffDays = d.startOf("day").diff(dayjs(stepProductionDate).startOf("day"), "day")
  if (diffDays < 0) return `le ${d.format("DD/MM/YYYY")}`
  if (diffDays === 0) return "aujourd'hui"
  if (diffDays === 1) return "demain"
  return `dans ${diffDays} jours`
}

const getLabelTitle = (pse) => {
  if (pse.isStepMerged) {
    return `${pse.productionStepSnapshot.name} (${pse.mergedProductionStepExecutions?.length})`
  }

  return pse.uniqueCode + " - " + pse.productionStepSnapshot?.name + " - " + pse.sectionName
}

/**
 * Labels of one PSE: one per section output and/or per packaging date, otherwise a single one.
 * @returns {Promise<{ title: string, stepProductionDate: string|Date, netWeight: number, packagingDateLabel?: string }[]>}
 */
const computePseLabels = async (pse) => {
  // ?? and not ||: a step weighed at 0 kg prints 0, not its theoretical weight
  const netWeight = pse.netWeight ?? pse.realWeight ?? pse.theoreticalNetWeight
  const title = getLabelTitle(pse)

  const productionDate = pse.productionDate || pse.stepProductionDate
  const packagingDatesData = computePackagingDatesData(
    pse.packagingExecutions || [],
    netWeight,
    productionDate
  )
  const hasMultiDates = packagingDatesData.length > 1
  // Use the weight distribution entered at weighing when available, otherwise the portions share
  const packagingDateWeights = pse.packagingDateWeights || {}
  const totalDistributedWeight = Object.values(packagingDateWeights).reduce((sum, weight) => sum + (Number(weight) || 0), 0)
  const getDateWeight = (dateRow) => {
    const distributedWeight = packagingDateWeights[dateRow.date]
    if (totalDistributedWeight > 0 && distributedWeight != null) return netWeight * Number(distributedWeight) / totalDistributedWeight
    return dateRow.theoreticalWeight
  }

  // Case A & C: section outputs (with or without multi-dates)
  if (pse.isSectionLastStep && pse.sectionOutputWeights) {
    // Fetch full section data if it's a Pointer
    let sectionData = pse.section
    if (pse.section?.__type === "Pointer" && pse.section?.className === "Section") {
      try {
        const sectionQuery = new Parse.Query("Section")
        sectionQuery.include(["sectionOutputs"])
        const section = await sectionQuery.get(pse.section.objectId)
        sectionData = section.toJSON()
      } catch (error) {
        console.error("Error fetching section:", error)
        sectionData = pse.section
      }
    }

    // Calculate weight per section output
    const weightPerSectionOutput = {}
    Object.keys(pse.sectionOutputWeights).forEach(key => {
      const parts = key.split("_")
      const sectionOutputUUID = parts.slice(0, -1).join("_")
      if (!weightPerSectionOutput[sectionOutputUUID]) {
        weightPerSectionOutput[sectionOutputUUID] = 0
      }
      weightPerSectionOutput[sectionOutputUUID] += parseFloat(pse.sectionOutputWeights[key])
    })

    const sectionOutputs = sectionData?.sectionOutputs || []

    if (Object.keys(weightPerSectionOutput).length > 0 && sectionOutputs.length > 0) {
      const outputLabels = await Promise.all(sectionOutputs.map(async output => {
        const truncatedTitle = title.split(" - ").slice(0, 2).join(" - ")
        const outPutTitle = output.name ?? `Partie ${output.percentage}%`
        const dataPse = await getPseWithSectionOutputs([output], pse.stepProductionDate, pse.recipe && pse.recipe.objectId || pse.recipe.id)
        const dataTitle = dataPse ? dataPse.get("sectionName") : "dressage"

        const outputWeight = weightPerSectionOutput[output.index] ||
          (output.percentage ? netWeight * (output.percentage / 100) :
            netWeight * (output.ingredients || []).reduce((acc, curr) => acc + (curr.netWeight || 0), 0) / (sectionData?.netWeight || 1))

        return { title: `${truncatedTitle} - ${outPutTitle} pour ${dataTitle}`, outputWeight }
      }))

      if (hasMultiDates) {
        // Case A: section outputs × packaging dates
        const results = []
        for (const dateRow of packagingDatesData) {
          const packagingDateLabel = `à barquetter ${getPrintDateLabel(dateRow.date, productionDate)}`
          const dateFraction = netWeight > 0 ? getDateWeight(dateRow) / netWeight : 0
          for (const { title: outputTitle, outputWeight } of outputLabels) {
            results.push({
              title: outputTitle,
              packagingDateLabel,
              stepProductionDate: productionDate,
              netWeight: outputWeight * dateFraction,
            })
          }
        }
        return results
      }

      // Case C: section outputs only
      return outputLabels.map(({ title: outputTitle, outputWeight }) => ({
        title: outputTitle,
        stepProductionDate: productionDate,
        netWeight: outputWeight,
      }))
    }
  }

  // Case B: multiple packaging dates only
  if (hasMultiDates) {
    return packagingDatesData.map(dateRow => ({
      title,
      packagingDateLabel: `à barquetter ${getPrintDateLabel(dateRow.date, productionDate)}`,
      stepProductionDate: productionDate,
      netWeight: getDateWeight(dateRow),
    }))
  }

  // Case D: single label
  return [{
    title,
    stepProductionDate: productionDate,
    netWeight,
  }]
}

// The PSE given to the print modal may carry only a few fields of its merged items (list views): read them again
const getMergedIndividualPSEs = async (mergedPSE) => {
  const pse = await getProductionStepExecution(mergedPSE.objectId, true, ["mergedProductionStepExecutions.productionStepExecution"])
  return (pse.mergedProductionStepExecutions || [])
    .map(merged => merged.productionStepExecution)
    .filter(individualPSE => individualPSE?.objectId)
}

/**
 * Labels to print for a PSE. A merged PSE prints the labels of each recipe it gathers,
 * as its own PSE would: title, weighed weight, section outputs and packaging dates of that recipe.
 */
export const computePrintableLabels = async (pse) => {
  if (!pse) return []
  if (!pse.isStepMerged) return computePseLabels(pse)

  let individualPSEs = []
  try {
    individualPSEs = await getMergedIndividualPSEs(pse)
  } catch (error) {
    console.error("Error fetching merged PSEs:", error)
  }
  // Without its recipes, the merged PSE keeps its single label with the total weight
  if (individualPSEs.length === 0) return computePseLabels(pse)

  // Every merged recipe runs the same step: keep the step name shown on the merged PSE
  const labelsByPSE = await Promise.all(individualPSEs.map(individualPSE => computePseLabels({
    ...individualPSE,
    productionStepSnapshot: { ...individualPSE.productionStepSnapshot, name: pse.productionStepSnapshot?.name },
  })))
  return labelsByPSE.flat()
}

// Labels of several PSEs printed at once, in their order
export const computePrintableLabelsForPSEs = async (pses = []) => {
  const labelsByPSE = await Promise.all(pses.map(computePrintableLabels))
  return labelsByPSE.flat()
}
